import { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

interface WhatsAppQRDialogProps {
  open: boolean;
  onClose: () => void;
  onConnected: () => void;
  baseUrl: string;
  token: string;
  sessionId: string;
}

const WhatsAppQRDialog = ({ open, onClose, onConnected, baseUrl, token, sessionId }: WhatsAppQRDialogProps) => {
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'qr' | 'connected' | 'error'>('loading');
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const createdRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    createdRef.current = false;
    setStatus('loading');
    setQrImage(null);

    const createAndPoll = async () => {
      if (!createdRef.current) {
        try {
          await fetch(`${baseUrl}/control/create?session=${encodeURIComponent(sessionId)}&access_token=${encodeURIComponent(token)}`, {
            headers: { 'x-api-key': token },
          });
        } catch {}
        createdRef.current = true;
      }

      const poll = async () => {
        try {
          const res = await fetch(`${baseUrl}/control/qr/${encodeURIComponent(sessionId)}`, {
            headers: { 'x-api-key': token },
          });
          const data = await res.json();

          if (data.connected) {
            setStatus('connected');
            if (pollRef.current) clearInterval(pollRef.current);
            setTimeout(() => {
              onConnected();
              onClose();
            }, 2000);
            return;
          }

          if (data.qr) {
            setQrImage(data.qr);
            setStatus('qr');
          }
        } catch {
          setStatus('error');
        }
      };

      await poll();
      pollRef.current = setInterval(poll, 4000);
    };

    createAndPoll();

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [open, baseUrl, token, sessionId, onConnected, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', py: 4, position: 'relative' }}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" gutterBottom>
          WhatsApp - {sessionId}
        </Typography>

        {status === 'loading' && (
          <Box sx={{ py: 4 }}>
            <CircularProgress sx={{ color: '#25D366' }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Preparing QR code...
            </Typography>
          </Box>
        )}

        {status === 'qr' && qrImage && (
          <Box>
            <img src={qrImage} alt="QR Code" style={{ width: 280, height: 280, borderRadius: 8 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              סרוק עם WhatsApp בנייד
            </Typography>
          </Box>
        )}

        {status === 'connected' && (
          <Box sx={{ py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#25D366' }} />
            <Typography variant="h6" sx={{ color: '#25D366', mt: 2 }}>
              מחובר!
            </Typography>
          </Box>
        )}

        {status === 'error' && (
          <Box sx={{ py: 3 }}>
            <Typography color="error">שגיאה בטעינת QR. נסה שוב.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppQRDialog;
